import { Button, Dropdown, Form, Input, Select, Space } from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import ActionThreeDotIcon from "../../../../assets/icons/ActionThreeDotIcon";
import AddIcon from "../../../../assets/icons/AddIcon";
import EditIcon from "../../../../assets/icons/EditIcon";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import SuccessModal from "../../../../components/common/SuccessModal";
import CustomTable from "../../../../components/ui/CustomTable";
import Loader from "../../../../components/ui/Loader";
import {
  useCreateAreaMutation,
  useGetAllAreaQuery,
  useGetAllZoneQuery,
  useGetSingleAreaQuery,
  useUpdateAreaMutation,
} from "../../../../features/cashCollection/geoSetup/geoSetupApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import {
  GetAllAreaDataParamsType,
  GetAllAreaDataType,
} from "../../../../types/cashCollection/geoSetup";
import { countParPageSerialNumber } from "../../../../utils/number";
import { capitalize } from "../../../../utils/string";
import { addKeyInArray } from "../../../../utils/utils";
import AddEditManageModal from "../Modal/AddEditManageModal";

export default function AreaList() {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [form] = Form.useForm();

  const {
    open: openAddModal,
    handleOpen: handleAddOpen,
    handleClose: handleAddClose,
  } = useModal<undefined, "ADD">();

  const {
    open: openSuccessModal,
    handleOpen: handleSuccessOpen,
    handleClose: handleSuccessClose,
  } = useModal<undefined, "SUCCESS">();

  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    filter,
    handleFilter,
    handleSearch,
    keyword,
  } = useTable<GetAllAreaDataParamsType>();

  // Get all Areas
  const { isError, error, data, isLoading } = useGetAllAreaQuery({
    params: {
      areaName: keyword,
      skip: undefined,
      page,
      limit,
    },
  });

  // Get single Area
  const {
    data: getSingleAreaData,
    isError: getSingleAreaIsError,
    error: getSingleAreaError,
    isLoading: getSingleAreaIsLoading,
  } = useGetSingleAreaQuery(selectedId || "", {
    skip: !selectedId,
  });

  // Create Area
  const [
    createArea,
    {
      isError: createAreaIsError,
      error: createAreaError,
      isLoading: createAreaIsLoading,
      isSuccess: createAreaIsSuccess,
    },
  ] = useCreateAreaMutation();

  // Update Area
  const [
    updateArea,
    {
      isError: updateAreaIsError,
      error: updateAreaError,
      isLoading: updateAreaIsLoading,
      isSuccess: updateAreaIsSuccess,
    },
  ] = useUpdateAreaMutation();

  // Get Zone
  const {
    isError: getAllZoneIsError,
    error: getAllZoneError,
    data: getAllZoneData,
    isLoading: getAllZoneIsLoading,
  } = useGetAllZoneQuery({
    params: {
      page: 1,
      limit: 100000000,
    },
  });

  // 🔹 Centralized error handling
  useError(isError, error, "Get all area fail");
  useError(getSingleAreaIsError, getSingleAreaError, "Get single area fail");
  useError(createAreaIsError, createAreaError, "Create area fail");
  useError(updateAreaIsError, updateAreaError, "Update area fail");
  useError(getAllZoneIsError, getAllZoneError, "Get all zone fail");

  const onFinish = (values: any) => {
    const newData = {
      ...values,
      active: true,
    };

    if (selectedId) {
      updateArea({
        id: selectedId,
        data: values,
      });
    } else {
      createArea(newData);
    }
  };

  const getModalTitle = () => {
    if (openAddModal.type === "ADD" && selectedId) {
      return "Area Successfully Updated!";
    }
    if (openAddModal.type === "ADD") {
      return "Area Successfully Added!";
    }
    return "";
  };

  const handleOkay = () => {
    handleCloseModal();
    handleSuccessClose();
  };

  const handleCloseModal = () => {
    setSelectedId(null);
    form.setFieldsValue({});
    form.resetFields();
    handleAddClose();
  };

  useEffect(() => {
    if (getSingleAreaData?.data && selectedId) {
      form.setFieldsValue({
        zoneId: getSingleAreaData?.data?.zone?.id,
        areaName: capitalize(getSingleAreaData?.data?.areaName),
      });
    }
  }, [getSingleAreaData?.data, selectedId, form]);

  useEffect(() => {
    if (createAreaIsSuccess) {
      handleSuccessOpen({ type: "SUCCESS" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createAreaIsSuccess]);

  useEffect(() => {
    if (updateAreaIsSuccess) {
      handleSuccessOpen({ type: "SUCCESS" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateAreaIsSuccess]);

  const columns: ColumnsType<GetAllAreaDataType> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },

    {
      title: <div className="text-nowrap">Zone</div>,
      dataIndex: "zone",
      align: "center",
      render: (val) => {
        return capitalize(val?.zoneName) || "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Area</div>,
      dataIndex: "areaName",
      align: "center",
      render: (val) => {
        return capitalize(val) || "N/A";
      },
    },
    // {
    //   title: <div className="text-nowrap">Created at</div>,
    //   dataIndex: "remaining",
    //   align: "center",
    //   render: (val) => {
    //     return val || "N/A";
    //   },
    // },
    {
      title: <div className="whitespace-nowrap">Status</div>,
      dataIndex: "active",
      align: "center",
      render: (val: boolean) => {
        const status = val ? "Active" : "Inactive";

        const statusClasses: Record<string, string> = {
          Active: "bg-green-100 text-green-700",
          Inactive: "bg-red-100 text-red-700",
        };

        return (
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${statusClasses[status]}`}
          >
            {status}
          </span>
        );
      },
    },

    {
      title: "Actions",
      dataIndex: "id",
      align: "center",
      render: (val: number, record: any) => {
        const items = [
          // {
          //   key: "active",
          //   label: (
          //     <div className="flex justify-between items-center pb-2 border-b border-[#D7DFE9]">
          //       <span className="text-gray-700">Active</span>
          //       <Switch defaultChecked size="small" />
          //     </div>
          //   ),
          // },
          {
            key: "edit",
            label: (
              <div
                className="flex items-center gap-2 justify-start text-slate-700 text-sm font-medium"
                onClick={() => {
                  setSelectedId(val);
                  handleAddOpen({ type: "ADD" });
                }}
              >
                <EditIcon />
                <span>Edit</span>
              </div>
            ),
          },
        ];

        return (
          <div className="flex justify-center items-center gap-2">
            <Dropdown
              menu={{
                items,
                className: "min-w-[170px] !h-[40px] ",
              }}
              trigger={["click"]}
              placement="bottomRight"
              arrow={{ pointAtCenter: true }}
            >
              <Button
                className="action-button disabled:!bg-[#ddd] !border-none"
                size="small"
                icon={<ActionThreeDotIcon />}
              />
            </Dropdown>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <SidebarLayout title="GEO Set-up">
        <CustomTable
          loading={isLoading}
          limit={limit}
          page={page}
          columns={columns}
          total={data?.data?.count}
          dataSource={addKeyInArray(data?.data?.rows)}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          filterSection={
            <>
              <Input
                suffix={<SearchIcon />}
                placeholder="Search Area"
                style={{ width: 215 }}
                allowClear
                onChange={handleSearch}
              />

              <Button
                type="primary"
                className="middle-button"
                icon={<AddIcon />}
                onClick={() => handleAddOpen({ type: "ADD" })}
              >
                New Area
              </Button>
            </>
          }
        />
      </SidebarLayout>

      {openAddModal?.type === "ADD" && (
        <AddEditManageModal
          open={openAddModal?.open}
          handleClose={handleCloseModal}
        >
          <div>
            <div className="justify-start text-zinc-700 text-xl font-medium">
              {selectedId ? "Update Area" : "Add New Area"}
            </div>
            {getAllZoneIsLoading || getSingleAreaIsLoading ? (
              <Loader />
            ) : (
              <div className="mt-5">
                <Form
                  className="custom-form"
                  layout="vertical"
                  requiredMark="optional"
                  size="large"
                  onFinish={onFinish}
                  form={form}
                >
                  <Form.Item
                    label="Zone"
                    name="zoneId"
                    rules={[
                      {
                        required: true,
                        message: "Please enter zone",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select Zone"
                      suffixIcon={<SelectDownIcon />}
                      showSearch
                      allowClear
                      className="w-full"
                      disabled={!!selectedId}
                      // value={filter?.district}
                      options={
                        getAllZoneData?.data?.rows?.map((val: any) => ({
                          label: capitalize(val?.zoneName),
                          value: val?.id, // value must match ID
                        })) || []
                      }
                      // onChange={handleDistrictChange}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Area Name"
                    name="areaName"
                    rules={[
                      { required: true, message: "Please enter area name" },
                    ]}
                  >
                    <Input placeholder="Enter Area Name" />
                  </Form.Item>

                  <Space className="w-full mt-5" size={12}>
                    <Button
                      className="normal-dark-button modal-button !h-[40px]"
                      onClick={handleCloseModal}
                      size="middle"
                    >
                      Cancel
                    </Button>

                    <Button
                      className="modal-button !h-[40px]"
                      type="primary"
                      size="middle"
                      htmlType="submit"
                      loading={createAreaIsLoading || updateAreaIsLoading}
                    >
                      {selectedId ? "Update Area" : "Add Area"}
                    </Button>
                  </Space>
                </Form>
              </div>
            )}
          </div>
        </AddEditManageModal>
      )}

      {openSuccessModal.type === "SUCCESS" && (
        <SuccessModal
          open={openSuccessModal?.open}
          handleClose={handleSuccessClose}
          handleOkay={handleOkay}
          data={openSuccessModal?.data}
          title={getModalTitle()}
        />
      )}
    </>
  );
}
